import classNames from 'classnames'
import { Fragment } from 'react'
import { AInformationPopup } from 'ui'

import { CComparisonTableBlock } from '../organisms/c-comparison-table'
import { ASvg } from '../atoms/a-svg'
import SvgCloseIcon from '../../svg/icons/stroke/close.svg'
import SvgCheckIcon from '../../svg/icons/fill/check-thin.svg'
import './m-comparison-table.css'

interface MComparisonTableProps {
  firstHeader: CComparisonTableBlock | undefined
  content: CComparisonTableBlock[]
  mainBlock: any
}

export const MComparisonTable = ({
  firstHeader,
  content,
  mainBlock,
}: MComparisonTableProps) => {
  const disclaimerTextItem = content.filter((row) => row.disclaimerText)[0]
  const disclaimerText = disclaimerTextItem
    ? disclaimerTextItem.disclaimerText
    : undefined

  const isFirstEnabledColumn = (column: '1' | '2' | '3') => {
    const disabled = mainBlock?.disableColumns ?? []

    if (column === '1') return !disabled.includes('1')
    if (column === '2') return disabled.includes('1') && !disabled.includes('2')
    if (column === '3') {
      return disabled.includes('1') && disabled.includes('2') && !disabled.includes('3')
    }

    return false
  }

  return (
    <div className="max-w-[800px] mx-auto text-gray-850">
      {firstHeader?.tableHeader && (
        <div className="grid grid-cols-[128px_1fr_1fr_1fr] sm:grid-cols-[2.5fr_1fr_1fr_1fr] w-full c-comparison-table__row__us text-neutral-dark-gray bg-gray-50 border border-gray-200 rounded-t-[10px] py-[10px] px-[12px] md:px-[24px]">
          <div className="empty" />

          {!mainBlock?.disableColumns?.includes('1') && (
            <div
              className={classNames(
                'text-center text-lg md:text-2xl font-semibold text-primary-teal',
                {
                  'c-comparison-table__row-accent': isFirstEnabledColumn('1'),
                }
              )}
            >
              <h4>{firstHeader.tableHeader[0].columnHeading1}</h4>
            </div>
          )}

          {!mainBlock?.disableColumns?.includes('2') && (
            <div
              className={classNames(
                'text-center text-sm sm:text-md md:text-xl font-semibold text-primary-dark-teal',
                {
                  'c-comparison-table__row-accent': isFirstEnabledColumn('2'),
                }
              )}
            >
              <h4>{firstHeader.tableHeader[0].columnHeading2}</h4>
            </div>
          )}

          {!mainBlock?.disableColumns?.includes('3') && (
            <div
              className={classNames(
                'text-center text-sm sm:text-md md:text-xl font-semibold text-primary-dark-teal',
                {
                  'c-comparison-table__row-accent': isFirstEnabledColumn('3'),
                }
              )}
            >
              <h4>{firstHeader.tableHeader[0].columnHeading3}</h4>
            </div>
          )}
        </div>
      )}

      {content.map((row, index) => {
        const isFooterNext = Boolean(
          content[index + 1] && content[index + 1].typeHandle === 'tableFooter'
        )

        return (
          <Fragment key={`second${index}`}>
            {row.typeHandle === 'tableHeader' && row.tableHeader && (
              <div
                className={classNames(
                  'grid grid-cols-[128px_1fr_1fr_1fr] sm:grid-cols-[2.5fr_1fr_1fr_1fr] w-full c-comparison-table__row__us pt-[11px] px-[12px] md:px-[24px] border border-b-0 mt-[4px]',
                  {
                    'border-t-0 mt-[0] pt-[9px]': index === 1,
                  }
                )}
              >
                <h3 className="w-full text-sm sm:text-lg md:text-xl font-semibold">
                  {row.tableHeader[0].mainHeading}
                </h3>

                {!mainBlock?.disableColumns?.includes('1') && (
                  <div
                    className={classNames({
                      'c-comparison-table__row-accent-col': isFirstEnabledColumn('1'),
                    })}
                  />
                )}

                {!mainBlock?.disableColumns?.includes('2') && (
                  <div
                    className={classNames({
                      'c-comparison-table__row-accent-col': isFirstEnabledColumn('2'),
                    })}
                  />
                )}

                {!mainBlock?.disableColumns?.includes('3') && (
                  <div
                    className={classNames({
                      'c-comparison-table__row-accent-col': isFirstEnabledColumn('3'),
                    })}
                  />
                )}
              </div>
            )}

            {row.typeHandle === 'tableBody' && row.tableBody && (
              <div
                className={classNames(
                  'grid grid-cols-[128px_1fr_1fr_1fr] sm:grid-cols-[2.5fr_1fr_1fr_1fr] items-center w-full c-comparison-table__row__us px-[12px] md:px-[24px] border border-y-0',
                  {
                    'border-b border-neutral-gray sm:border-b-0':
                      content[index + 1] &&
                      content[index + 1].typeHandle !== 'tableBody',
                    'sm:border-b-[1px] sm:border-neutral-gray':
                      content[index + 1] &&
                      content[index + 1].typeHandle !== 'tableBody' &&
                      content[index + 1].typeHandle !== 'tableFooter',
                    'rounded-b-[10px] border-b border-neutral-gray':
                      index === content.length - 1,
                  }
                )}
              >
                <div className="w-full c-comparison-table__row__us-heading text-xs sm:text-sm md:text-lg pr-[4px]">
                  <span>{row.tableBody[0].rowHeading}</span>

                  {isFooterNext && (
                    <>
                      <div className="md:hidden inline-block ml-[10px]">
                        <AInformationPopup
                          content={
                            content[index + 1].tableFooter?.[0]?.mainNote ?? ''
                          }
                        />
                      </div>

                      <div className="hidden md:block px-[16px] w-full text-[11px] text-center mt-1 text-gray-850">
                        {content[index + 1].tableFooter?.[0]?.mainNote}
                      </div>
                    </>
                  )}
                </div>

                {!mainBlock?.disableColumns?.includes('1') && (
                  <div
                    className={classNames(
                      'c-comparison-table__row__us-svg-cont relative',
                      {
                        'c-comparison-table__row-accent-col': isFirstEnabledColumn('1'),
                      }
                    )}
                  >
                    {row.tableBody[0].data1V2 === 'check' &&  <ASvg
                        aria-hidden="true"
                        className="w-8 h-8 md:w-10 md:h-10 fill-current text-primary-teal"
                        svg={SvgCheckIcon}
                    />}
                    {row.tableBody[0].data1V2 === 'na' && <div
                        className='a-svg  text-center w-8 h-8 md:w-10 md:h-10 stroke-current text-gray-200'>N/A</div>}

                    {row.tableBody[0].data1V2 === 'x' && <ASvg
                        aria-hidden="true"
                        className="w-8 h-8 md:w-10 md:h-10 stroke-current text-gray-200"
                        svg={SvgCloseIcon}
                    />}

                    {Boolean(row.tableBody[0].data1Asterisks) && (
                      <span className="absolute left-1/2 -translate-x-1/2 top-[36px] md:top-[44px] text-xs whitespace-nowrap">
                        {row.tableBody[0].data1Asterisks}
                      </span>
                    )}
                    {Boolean(row.tableBody[0].data1Superscript) && (
                      <span className="absolute left-1/2 -translate-x-1/2 top-[36px] md:top-[44px] text-xs whitespace-nowrap">
                        {row.tableBody[0].data1Superscript}
                      </span>
                    )}
                    {isFooterNext &&
                      content[index + 1].tableFooter?.[0]?.columnNote1 && (
                        <div className="px-[16px] w-full text-[11px] text-center">
                          {content[index + 1].tableFooter?.[0]?.columnNote1}
                        </div>
                      )}
                  </div>
                )}

                {!mainBlock?.disableColumns?.includes('2') && (
                  <div
                    className={classNames(
                      'c-comparison-table__row__us-svg-cont relative',
                      {
                        'c-comparison-table__row-accent-col': isFirstEnabledColumn('2'),
                      }
                    )}
                  >
                    {row.tableBody[0].data2V2 === 'check' &&  <ASvg
                        aria-hidden="true"
                        className="w-8 h-8 md:w-10 md:h-10 fill-current text-primary-teal"
                        svg={SvgCheckIcon}
                    />}
                    {row.tableBody[0].data2V2 === 'na' && <div
                        className='a-svg  text-center w-8 h-8 md:w-10 md:h-10 stroke-current text-gray-200'>N/A</div>}

                    {row.tableBody[0].data2V2 === 'x' && <ASvg
                        aria-hidden="true"
                        className="w-8 h-8 md:w-10 md:h-10 stroke-current text-gray-200"
                        svg={SvgCloseIcon}
                    />}
                    {Boolean(row.tableBody[0].data2Asterisks) && (
                      <span className="absolute left-1/2 -translate-x-1/2 top-[36px] md:top-[44px] text-xs whitespace-nowrap">
                        {row.tableBody[0].data2Asterisks}
                      </span>
                    )}
                    {Boolean(row.tableBody[0].data2Superscript) && (
                      <span className="absolute left-1/2 -translate-x-1/2 top-[36px] md:top-[44px] text-xs whitespace-nowrap">
                        {row.tableBody[0].data2Superscript}
                      </span>
                    )}
                    {isFooterNext &&
                      content[index + 1].tableFooter?.[0]?.columnNote2 && (
                        <div className="px-[16px] w-full text-[11px] text-center">
                          {content[index + 1].tableFooter?.[0]?.columnNote2}
                        </div>
                      )}
                  </div>
                )}

                {!mainBlock?.disableColumns?.includes('3') && (
                  <div
                    className={classNames(
                      'c-comparison-table__row__us-svg-cont relative',
                      {
                        'c-comparison-table__row-accent-col': isFirstEnabledColumn('3'),
                      }
                    )}
                  >
                    {row.tableBody[0].data3V2 === 'check' &&  <ASvg
                        aria-hidden="true"
                        className="w-8 h-8 md:w-10 md:h-10 fill-current text-primary-teal"
                        svg={SvgCheckIcon}
                    />}
                    {row.tableBody[0].data3V2 === 'na' && <div
                        className='a-svg  text-center w-8 h-8 md:w-10 md:h-10 stroke-current text-gray-200'>N/A</div>}

                    {row.tableBody[0].data3V2 === 'x' && <ASvg
                        aria-hidden="true"
                        className="w-8 h-8 md:w-10 md:h-10 stroke-current text-gray-200"
                        svg={SvgCloseIcon}
                    />}
                    {Boolean(row.tableBody[0].data3Asterisks) && (
                      <span className="absolute left-1/2 -translate-x-1/2 top-[36px] md:top-[44px] text-xs whitespace-nowrap">
                        {row.tableBody[0].data3Asterisks}
                      </span>
                    )}
                    {Boolean(row.tableBody[0].data3Superscript) && (
                      <span className="absolute left-1/2 -translate-x-1/2 top-[36px] md:top-[44px] text-xs whitespace-nowrap">
                        {row.tableBody[0].data3Superscript}
                      </span>
                    )}
                    {isFooterNext &&
                      content[index + 1].tableFooter?.[0]?.columnNote3 && (
                        <div className="px-[16px] w-full text-[11px] text-center">
                          {content[index + 1].tableFooter?.[0]?.columnNote3}
                        </div>
                      )}
                  </div>
                )}
              </div>
            )}
          </Fragment>
        )
      })}

      {disclaimerText ? (
        <div
          className="mt-10 text-xs"
          dangerouslySetInnerHTML={{
            __html: disclaimerText,
          }}
        />
      ) : (
        <div className="mt-10 text-xs">
          <p>UV: ultraviolet.</p>
          <p>
            * There is a limit flexibility if future procedures involve the
            cornea.
          </p>
          <p>** Not applicable</p>
          <p>*** Limited</p>
        </div>
      )}
    </div>
  )
}
